import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { Button } from "../design/button";
import { FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Input } from "../design/input";
import { Field } from "@headlessui/react";

import logo from "../assets/logo.png";
import { Link } from "../design/link";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/dashboard");
    } catch (err: any) {
      console.error("Authentication error", err);
      setError(err.message || "Failed to sign in. Please check your credentials.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gallery-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <form
          method="post"
          className="bg-white border border-gallery-200 rounded-sm p-8"
          onSubmit={handleSubmit}
        >
          <div className="text-center mb-8">
            <img src={logo} alt="Art Collection" className="w-16 h-16 mx-auto mb-6" />
            <h1 className="font-display text-2xl text-gallery-900 mb-2">
              Welcome back
            </h1>
            <p className="text-sm text-gallery-500">
              Sign in to your Art Collection account
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-sm">
              <p className="text-sm text-rose-700">{error}</p>
            </div>
          )}

          <Fieldset>
            <FieldGroup>
              <Field>
                <Label htmlFor="email" className="text-sm font-medium text-gallery-700 mb-1.5 block">
                  Email
                </Label>
                <Input
                  id="email"
                  aria-label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full"
                />
              </Field>
              <Field>
                <Label htmlFor="password" className="text-sm font-medium text-gallery-700 mb-1.5 block">
                  Password
                </Label>
                <Input
                  id="password"
                  aria-label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full"
                />
              </Field>
            </FieldGroup>

            <div className="mt-6">
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Signing in..." : "Sign in"}
              </Button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gallery-500">
                Don't have an account?{" "}
                <Link href="/register" className="text-gallery-900 font-medium hover:text-gallery-700">
                  Create one
                </Link>
              </p>
            </div>
          </Fieldset>
        </form>

        <p className="mt-8 text-center text-xs text-gallery-400">
          Art Collection Partner Portal
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
